// EPIC-P05-07: Admin Badge Management API
// API functions for badge admin operations

import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

if (!SUPABASE_URL) {
  throw new Error('VITE_SUPABASE_URL not configured');
}

export interface BadgeListItem {
  id: string;
  badge_key: string;
  title: string;
  description: string;
  category: 'analysis' | 'practice' | 'streak' | 'milestone' | 'social';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  is_active: boolean;
  updated_at: string;
  created_at: string;
  icon_path: string | null;
}

export interface BadgeDetail extends BadgeListItem {
  icon: string | null;
  criteria: any;
}

export interface BadgeCreateInput {
  badge_key: string;
  title: string;
  description: string;
  category: 'analysis' | 'practice' | 'streak' | 'milestone' | 'social';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  criteria: any;
  icon_path?: string | null;
  is_active?: boolean;
}

export interface BadgeUpdateInput {
  id: string;
  badge_key?: string;
  title?: string;
  description?: string;
  category?: 'analysis' | 'practice' | 'streak' | 'milestone' | 'social';
  rarity?: 'common' | 'rare' | 'epic' | 'legendary';
  criteria?: any;
  icon_path?: string | null;
  is_active?: boolean;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  rarity?: string;
  is_active?: boolean | string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  badges?: T[];
  audit_logs?: T[];
  users?: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

export async function getBadgesList(params: PaginationParams = {}): Promise<PaginatedResponse<BadgeListItem>> {
  try {
    const headers = await getAuthHeaders();
    const url = new URL(`${SUPABASE_URL}/functions/v1/admin-badges-list`);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });

    console.log('[getBadgesList] Fetching badges from:', url.toString());
    console.log('[getBadgesList] Headers:', { ...headers, Authorization: 'Bearer ***' });
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    console.log('[getBadgesList] Response status:', response.status, response.statusText);
    console.log('[getBadgesList] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[getBadgesList] ERROR - Response not OK');
      console.error('[getBadgesList] Status:', response.status, response.statusText);
      console.error('[getBadgesList] Error body:', errorText);
      
      let error;
      try {
        error = JSON.parse(errorText);
      } catch {
        error = { error: errorText || 'Failed to fetch badges' };
      }
      
      const errorMessage = error.error || `Failed to fetch badges: ${response.status} ${response.statusText}`;
      console.error('[getBadgesList] Throwing error:', errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[getBadgesList] SUCCESS - Received', data?.badges?.length || 0, 'badges');
    return data;
  } catch (error) {
    console.error('[getBadgesList] EXCEPTION CAUGHT');
    console.error('[getBadgesList] Error type:', typeof error);
    console.error('[getBadgesList] Error:', error);
    if (error instanceof Error) {
      console.error('[getBadgesList] Error message:', error.message);
      console.error('[getBadgesList] Error stack:', error.stack);
    }
    throw error;
  }
}

export async function createBadge(input: BadgeCreateInput): Promise<{ badge: BadgeDetail }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-badge-create`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create badge' }));
    throw new Error(error.error || 'Failed to create badge');
  }

  return response.json();
}

export async function updateBadge(input: BadgeUpdateInput): Promise<{ badge: BadgeDetail }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-badge-update`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update badge' }));
    throw new Error(error.error || 'Failed to update badge');
  }

  return response.json();
}

export async function getBadgeDetail(badgeId: string): Promise<{ badge: BadgeDetail; audit_history: any[] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-badge-detail?id=${badgeId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch badge detail' }));
    throw new Error(error.error || 'Failed to fetch badge detail');
  }

  return response.json();
}

export async function getBadgeUsers(badgeId: string, params: PaginationParams = {}): Promise<PaginatedResponse<any>> {
  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/admin-badge-users`);
  url.searchParams.append('badge_id', badgeId);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value));
    }
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch badge users' }));
    throw new Error(error.error || 'Failed to fetch badge users');
  }

  return response.json();
}

export async function revokeBadge(userBadgeId: string): Promise<{ success: boolean }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-badge-revoke?user_badge_id=${userBadgeId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to revoke badge' }));
    throw new Error(error.error || 'Failed to revoke badge');
  }

  return response.json();
}

export async function evaluateCriteria(criteria: any, sampleData?: any): Promise<any> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-badge-evaluate-criteria`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ criteria, sample_data: sampleData }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to evaluate criteria' }));
    throw new Error(error.error || 'Failed to evaluate criteria');
  }

  return response.json();
}

export async function getAuditLog(params: PaginationParams = {}): Promise<PaginatedResponse<any>> {
  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/admin-badge-audit-log`);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value));
    }
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch audit log' }));
    throw new Error(error.error || 'Failed to fetch audit log');
  }

  return response.json();
}

